import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import CsvImportModal from "@/components/CsvImportModal";
import {
  Plus,
  UserPlus,
  Copy,
  Check,
  ExternalLink,
  Trash2,
  BarChart3,
  ArrowLeft,
  ClipboardList,
  Users,
  Crown,
  UserCog,
  RefreshCw,
  X,
  LogOut,
  QrCode,
  Printer,
  Upload,
  GitCompare,
  Pencil,
  Search,
  Filter,
} from "lucide-react";

const POSITIONS = [
  "Torwart",
  "Innenverteidiger",
  "Außenverteidiger",
  "Sechser",
  "Achter",
  "Zehner",
  "Flügel",
  "Stürmer",
];

export default function TeamDetail() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [pName, setPName] = useState("");
  const [pPos, setPPos] = useState("Sechser");
  const [pNum, setPNum] = useState("");
  const [copied, setCopied] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [qrPlayer, setQrPlayer] = useState(null);
  const [showCsv, setShowCsv] = useState(false);
  const [editPlayer, setEditPlayer] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", position: "", jersey_number: "",
    birthdate: "", height_cm: "", weight_kg: "", strong_foot: "",
    attendance_present: "", attendance_absent: "", injury_note: "",
  });
  const [editTab, setEditTab] = useState("basic"); // basic | training | notes
  const [newNote, setNewNote] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPosition, setFilterPosition] = useState("all");
  const [sortBy, setSortBy] = useState("jersey");
  const [selected, setSelected] = useState([]);
  const [bulkPosition, setBulkPosition] = useState("Sechser");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t, ps, ms] = await Promise.all([
        api.get(`/teams/${teamId}`),
        api.get(`/teams/${teamId}/players`),
        api.get(`/teams/${teamId}/members`),
      ]);
      setTeam(t.data);
      setPlayers(ps.data.players);
      setMembers(ms.data.members);
      setInviteCode(ms.data.invite_code);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [teamId]);

  const addPlayer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/players", {
        team_id: teamId,
        name: pName,
        position: pPos,
        jersey_number: pNum ? parseInt(pNum, 10) : null,
      });
      toast.success("Spieler hinzugefügt");
      setShowAdd(false);
      setPName("");
      setPNum("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removePlayer = async (id) => {
    if (!confirm("Spieler wirklich löschen?")) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Spieler gelöscht");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEdit = (p) => {
    setEditPlayer(p);
    setEditTab("basic");
    setNewNote("");
    setEditForm({
      name: p.name || "",
      position: p.position || "Sechser",
      jersey_number: p.jersey_number ?? "",
      birthdate: p.birthdate || "",
      height_cm: p.height_cm ?? "",
      weight_kg: p.weight_kg ?? "",
      strong_foot: p.strong_foot || "",
      attendance_present: p.attendance_present ?? "",
      attendance_absent: p.attendance_absent ?? "",
      injury_note: p.injury_note || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editPlayer) return;
    const toInt = (v) => (v === "" || v === null ? null : parseInt(v, 10));
    try {
      await api.patch(`/players/${editPlayer.id}`, {
        name: editForm.name,
        position: editForm.position || null,
        jersey_number: toInt(editForm.jersey_number),
        birthdate: editForm.birthdate || null,
        height_cm: toInt(editForm.height_cm),
        weight_kg: toInt(editForm.weight_kg),
        strong_foot: editForm.strong_foot || null,
        attendance_present: toInt(editForm.attendance_present),
        attendance_absent: toInt(editForm.attendance_absent),
        injury_note: editForm.injury_note || null,
      });
      toast.success("Spieler aktualisiert");
      setEditPlayer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!editPlayer || !newNote.trim()) return;
    try {
      const { data: note } = await api.post(
        `/players/${editPlayer.id}/notes`,
        { text: newNote.trim() }
      );
      const updated = {
        ...editPlayer,
        notes: [...(editPlayer.notes || []), note],
      };
      setEditPlayer(updated);
      setPlayers((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
      setNewNote("");
      toast.success("Notiz hinzugefügt");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeNote = async (noteId) => {
    if (!editPlayer) return;
    try {
      await api.delete(`/players/${editPlayer.id}/notes/${noteId}`);
      const updated = {
        ...editPlayer,
        notes: (editPlayer.notes || []).filter((n) => n.id !== noteId),
      };
      setEditPlayer(updated);
      setPlayers((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
      toast.success("Notiz gelöscht");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = visiblePlayers.map((p) => p.id);
    if (visibleIds.every((id) => selected.includes(id))) {
      setSelected((s) => s.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((s) => Array.from(new Set([...s, ...visibleIds])));
    }
  };

  const bulkDelete = async () => {
    if (
      !confirm(
        `${selected.length} Spieler wirklich löschen? Ergebnisse gehen verloren.`
      )
    )
      return;
    try {
      await api.post("/players/bulk-action", {
        player_ids: selected,
        action: "delete",
      });
      toast.success(`${selected.length} Spieler gelöscht`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkSetPosition = async () => {
    try {
      await api.post("/players/bulk-action", {
        player_ids: selected,
        action: "set_position",
        position: bulkPosition,
      });
      toast.success(`Position für ${selected.length} Spieler gesetzt`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Sichtbare Spieler nach Filter/Suche + Sortierung
  const visiblePlayers = players
    .filter((p) => {
      if (
        search &&
        !p.name.toLowerCase().includes(search.toLowerCase()) &&
        !(p.position || "").toLowerCase().includes(search.toLowerCase())
      )
        return false;
      if (filterStatus === "completed" && p.test_status !== "completed") return false;
      if (filterStatus === "pending" && p.test_status === "completed") return false;
      if (filterPosition !== "all" && p.position !== filterPosition) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name, "de");
      if (sortBy === "status") {
        const rank = (p) => (p.test_status === "completed" ? 0 : 1);
        const diff = rank(a) - rank(b);
        return diff !== 0 ? diff : a.name.localeCompare(b.name, "de");
      }
      // jersey (default)
      const na = a.jersey_number ?? 999;
      const nb = b.jersey_number ?? 999;
      return na - nb;
    });
  const availablePositions = Array.from(
    new Set(players.map((p) => p.position).filter(Boolean))
  ).sort();

  const testLink = (token) => `${window.location.origin}/test/${token}`;

  const copyLink = (token) => {
    navigator.clipboard.writeText(testLink(token));
    setCopied(token);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Link kopiert");
  };

  const copyInviteCode = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1500);
    toast.success("Code kopiert");
  };

  const regenerateCode = async () => {
    if (!confirm("Neuen Code erzeugen? Der alte Code wird ungültig.")) return;
    try {
      const { data } = await api.post(`/teams/${teamId}/regenerate-invite`);
      setInviteCode(data.invite_code);
      toast.success("Neuer Code erzeugt");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeMember = async (userId, memberName) => {
    if (!confirm(`${memberName} aus dem Team entfernen?`)) return;
    try {
      await api.delete(`/teams/${teamId}/members/${userId}`);
      toast.success("Mitglied entfernt");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const leaveTeam = async () => {
    if (!confirm("Team wirklich verlassen? Du verlierst den Zugriff.")) return;
    try {
      await api.post(`/teams/${teamId}/leave`);
      toast.success("Team verlassen");
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const completedCount = players.filter((p) => p.test_status === "completed").length;

  return (
    <div className="mx-auto max-w-7xl px-6 py-12">
      <Link
        to="/dashboard"
        data-testid="team-back-btn"
        className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#CCFF00] mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zum Dashboard
      </Link>

      {team && (
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / TEAM · {team.age_group}
            </div>
            <h1
              data-testid="team-name-heading"
              className="font-heading font-black text-4xl md:text-5xl tracking-tight"
            >
              {team.name}
            </h1>
            {team.description && (
              <div className="text-white/50 mt-2">{team.description}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {completedCount > 0 && (
              <Link
                to={`/teams/${teamId}/analysis`}
                data-testid="team-analysis-link"
                className="btn-ghost"
              >
                <BarChart3 className="w-5 h-5" />
                Team-Analyse ({completedCount})
              </Link>
            )}
            {completedCount >= 2 && (
              <Link
                to={`/teams/${teamId}/compare`}
                data-testid="team-compare-link"
                className="btn-ghost"
              >
                <GitCompare className="w-5 h-5" />
                Vergleichen
              </Link>
            )}
            {players.length > 0 && (
              <Link
                to={`/teams/${teamId}/qr-print`}
                data-testid="team-qr-print-link"
                className="btn-ghost"
              >
                <Printer className="w-5 h-5" />
                QR-Aushang
              </Link>
            )}
            <button
              data-testid="team-members-btn"
              onClick={() => setShowMembers(true)}
              className="btn-ghost"
            >
              <Users className="w-5 h-5" />
              Trainer ({members.length}/5)
            </button>
            <button
              data-testid="team-csv-import-btn"
              onClick={() => setShowCsv(true)}
              className="btn-ghost"
            >
              <Upload className="w-5 h-5" />
              CSV-Import
            </button>
            <button
              data-testid="team-add-player-btn"
              onClick={() => setShowAdd(true)}
              className="btn-primary"
            >
              <UserPlus className="w-5 h-5" />
              Spieler hinzufügen
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-white/40 font-display tracking-widest">Wird geladen …</div>
      ) : players.length === 0 ? (
        <div className="border border-white/10 border-dashed rounded-lg p-16 text-center">
          <UserPlus className="w-10 h-10 text-[#CCFF00] mx-auto mb-4" />
          <h3 className="font-heading font-bold text-2xl mb-2">Noch keine Spieler</h3>
          <p className="text-white/60 mb-6">
            Füge Spieler hinzu, um Test-Links zu generieren.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="empty-add-player-btn"
            className="btn-primary"
          >
            <Plus className="w-5 h-5" />
            Ersten Spieler anlegen
          </button>
        </div>
      ) : (
        <>
          {/* Filter Bar */}
          <div
            data-testid="players-filter-bar"
            className="mb-4 flex flex-col sm:flex-row gap-2 sm:gap-3"
          >
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="players-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nach Name oder Position suchen …"
                className="w-full bg-[#111214] border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-white text-sm focus:border-[#CCFF00] outline-none"
              />
            </div>
            <select
              data-testid="players-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#111214] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:border-[#CCFF00] outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="completed">Nur abgeschlossen</option>
              <option value="pending">Nur ausstehend</option>
            </select>
            <select
              data-testid="players-filter-position"
              value={filterPosition}
              onChange={(e) => setFilterPosition(e.target.value)}
              className="bg-[#111214] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:border-[#CCFF00] outline-none"
            >
              <option value="all">Alle Positionen</option>
              {availablePositions.map((pos) => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </select>
            <select
              data-testid="players-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#111214] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:border-[#CCFF00] outline-none"
            >
              <option value="jersey">Sortieren: Nr.</option>
              <option value="name">Sortieren: Name</option>
              <option value="status">Sortieren: Status</option>
            </select>
            {(search || filterStatus !== "all" || filterPosition !== "all") && (
              <button
                data-testid="players-filter-clear"
                onClick={() => {
                  setSearch("");
                  setFilterStatus("all");
                  setFilterPosition("all");
                }}
                className="btn-ghost !py-2 !px-3 text-xs"
                title="Filter zurücksetzen"
              >
                <X className="w-4 h-4" />
                Zurücksetzen
              </button>
            )}
          </div>

          {visiblePlayers.length === 0 && (
            <div className="border border-white/10 border-dashed rounded-md p-10 text-center text-white/50 mb-4">
              Kein Spieler passt zum Filter.
            </div>
          )}

          {selected.length > 0 && (
            <div
              data-testid="bulk-action-bar"
              className="mb-3 p-3 rounded-md border border-[#CCFF00]/40 bg-[#CCFF00]/5 flex flex-wrap items-center gap-3"
            >
              <div className="font-heading font-bold text-sm">
                {selected.length} ausgewählt
              </div>
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                <select
                  data-testid="bulk-position-select"
                  value={bulkPosition}
                  onChange={(e) => setBulkPosition(e.target.value)}
                  className="bg-[#0a0a0b] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-[#CCFF00] outline-none"
                >
                  {POSITIONS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
                <button
                  data-testid="bulk-set-position-btn"
                  onClick={bulkSetPosition}
                  className="btn-ghost !py-1.5 !px-3 text-xs"
                >
                  Position setzen
                </button>
                <button
                  data-testid="bulk-delete-btn"
                  onClick={bulkDelete}
                  className="btn-ghost !py-1.5 !px-3 text-xs border-red-500/40 hover:border-red-500/60 hover:bg-red-500/5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Löschen
                </button>
                <button
                  data-testid="bulk-clear-btn"
                  onClick={() => setSelected([])}
                  className="text-xs text-white/50 hover:text-white ml-auto"
                >
                  Auswahl aufheben
                </button>
              </div>
            </div>
          )}

          {/* Mobile: card layout */}
          <div className="md:hidden space-y-3">
            {visiblePlayers.map((p) => {
              const completed = p.test_status === "completed";
              const CardTag = completed ? Link : "div";
              const cardProps = completed
                ? {
                    to: `/players/${p.id}`,
                    "data-testid": `player-card-mobile-${p.id}`,
                  }
                : { "data-testid": `player-card-mobile-${p.id}` };
              return (
                <CardTag
                  key={p.id}
                  {...cardProps}
                  className={`block p-4 border rounded-md bg-[#111214] transition-colors ${
                    completed
                      ? "border-[#CCFF00]/30 hover:border-[#CCFF00]/60 active:bg-[#CCFF00]/5"
                      : "border-white/10"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-11 h-11 border border-white/15 rounded-sm flex items-center justify-center font-heading font-black text-white/70 flex-shrink-0">
                        {p.jersey_number || "–"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-lg leading-tight truncate">
                          {p.name}
                        </div>
                        <div className="text-xs text-white/60 mt-0.5">
                          {p.position || "–"}
                        </div>
                      </div>
                    </div>
                    {completed ? (
                      <span className="inline-flex items-center gap-1 text-[#CCFF00] font-semibold text-[10px] uppercase tracking-widest flex-shrink-0 mt-1">
                        <span className="w-1.5 h-1.5 bg-[#CCFF00] rounded-full" />
                        Fertig
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-white/50 font-semibold text-[10px] uppercase tracking-widest flex-shrink-0 mt-1">
                        <span className="w-1.5 h-1.5 bg-white/30 rounded-full" />
                        Offen
                      </span>
                    )}
                  </div>

                  {completed ? (
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                      <span className="text-xs font-heading font-bold text-[#CCFF00]">
                        Profil ansehen →
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`player-edit-mobile-${p.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openEdit(p);
                          }}
                          className="text-white/40 hover:text-[#CCFF00] p-1"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`player-delete-mobile-${p.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removePlayer(p.id);
                          }}
                          className="text-white/30 hover:text-red-400 p-1"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                      <button
                        data-testid={`player-qr-mobile-${p.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQrPlayer(p);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-[#CCFF00]/40 rounded-md text-[#CCFF00] font-heading font-bold text-xs uppercase tracking-widest active:bg-[#CCFF00]/10"
                      >
                        <QrCode className="w-4 h-4" />
                        QR
                      </button>
                      <button
                        data-testid={`player-copy-link-mobile-${p.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          copyLink(p.test_token);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-white/15 rounded-md text-white/80 font-heading font-bold text-xs uppercase tracking-widest active:bg-white/5"
                      >
                        {copied === p.test_token ? (
                          <>
                            <Check className="w-4 h-4" /> Kopiert
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> Link
                          </>
                        )}
                      </button>
                      <button
                        data-testid={`player-edit-mobile-${p.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openEdit(p);
                        }}
                        className="p-2 text-white/50 hover:text-[#CCFF00] border border-white/10 rounded-md"
                        title="Bearbeiten"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`player-delete-mobile-${p.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removePlayer(p.id);
                        }}
                        className="p-2 text-white/30 hover:text-red-400 border border-white/10 rounded-md"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </CardTag>
              );
            })}
          </div>

          {/* Desktop: table layout */}
          <div className="hidden md:block border border-white/10 rounded-md bg-[#111214] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left font-display text-xs tracking-[0.2em] text-white/40">
                  <th className="p-4 w-8">
                    <input
                      data-testid="bulk-select-all"
                      type="checkbox"
                      checked={
                        visiblePlayers.length > 0 &&
                        visiblePlayers.every((p) => selected.includes(p.id))
                      }
                      onChange={toggleSelectAllVisible}
                      className="accent-[#CCFF00] cursor-pointer"
                    />
                  </th>
                  <th className="p-4">Nr</th>
                  <th className="p-4">Name</th>
                  <th className="p-4">Position</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Testlink</th>
                  <th className="p-4 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {visiblePlayers.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`player-row-${p.id}`}
                    className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
                  >
                    <td className="p-4">
                      <input
                        data-testid={`bulk-select-${p.id}`}
                        type="checkbox"
                        checked={selected.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        className="accent-[#CCFF00] cursor-pointer"
                      />
                    </td>
                    <td className="p-4 font-display text-white/60">
                      {p.jersey_number || "-"}
                    </td>
                    <td className="p-4 font-heading font-bold">{p.name}</td>
                    <td className="p-4 text-white/70">{p.position || "-"}</td>
                    <td className="p-4">
                      {p.test_status === "completed" ? (
                        <span className="inline-flex items-center gap-1.5 text-[#CCFF00] font-semibold text-xs uppercase tracking-widest">
                          <span className="w-1.5 h-1.5 bg-[#CCFF00] rounded-full" />
                          Abgeschlossen
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-white/50 font-semibold text-xs uppercase tracking-widest">
                          <span className="w-1.5 h-1.5 bg-white/30 rounded-full" />
                          Ausstehend
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-white/60 bg-black/40 px-2 py-1 rounded truncate max-w-[180px]">
                          /test/{p.test_token.slice(0, 10)}…
                        </code>
                        <button
                          data-testid={`player-copy-link-${p.id}`}
                          onClick={() => copyLink(p.test_token)}
                          className="p-1.5 text-white/60 hover:text-[#CCFF00] transition-colors"
                          title="Link kopieren"
                        >
                          {copied === p.test_token ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          data-testid={`player-qr-${p.id}`}
                          onClick={() => setQrPlayer(p)}
                          className="p-1.5 text-white/60 hover:text-[#CCFF00] transition-colors"
                          title="QR-Code anzeigen"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <a
                          href={testLink(p.test_token)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-white/60 hover:text-[#CCFF00] transition-colors"
                          title="Test öffnen"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {p.test_status === "completed" && (
                          <Link
                            to={`/players/${p.id}`}
                            data-testid={`player-view-profile-${p.id}`}
                            className="text-xs font-heading font-bold text-[#CCFF00] hover:underline"
                          >
                            Profil ansehen
                          </Link>
                        )}
                        <button
                          data-testid={`player-edit-${p.id}`}
                          onClick={() => openEdit(p)}
                          className="text-white/40 hover:text-[#CCFF00] transition-colors p-1"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`player-delete-${p.id}`}
                          onClick={() => removePlayer(p.id)}
                          className="text-white/30 hover:text-red-400 transition-colors p-1"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
             onClick={() => setShowAdd(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={addPlayer}
            data-testid="add-player-modal"
            className="w-full max-w-md bg-[#111214] border border-white/10 rounded-md p-8"
          >
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
              / NEUER SPIELER
            </div>
            <h2 className="font-heading font-black text-2xl mb-6">
              Spieler hinzufügen
            </h2>
            <div className="space-y-4">
              <div>
                <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                  Name
                </label>
                <input
                  data-testid="add-player-name"
                  required
                  value={pName}
                  onChange={(e) => setPName(e.target.value)}
                  placeholder="Vorname Nachname"
                  className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                    Position
                  </label>
                  <select
                    data-testid="add-player-position"
                    value={pPos}
                    onChange={(e) => setPPos(e.target.value)}
                    className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                  >
                    {POSITIONS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                    Rückennummer
                  </label>
                  <input
                    data-testid="add-player-number"
                    type="number"
                    min="1"
                    max="99"
                    value={pNum}
                    onChange={(e) => setPNum(e.target.value)}
                    className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="btn-ghost flex-1 justify-center"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="add-player-submit"
                className="btn-primary flex-1 justify-center"
              >
                Hinzufügen
              </button>
            </div>
          </form>
        </div>
      )}

      {showMembers && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setShowMembers(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="members-modal"
            className="w-full max-w-lg bg-[#111214] border border-white/10 rounded-md p-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
                  / TRAINER-TEAM
                </div>
                <h2 className="font-heading font-black text-2xl">
                  {members.length} / 5 Trainer
                </h2>
              </div>
              <button
                onClick={() => setShowMembers(false)}
                className="text-white/40 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {inviteCode && (
              <div className="mb-6 p-4 border border-[#CCFF00]/30 bg-[#CCFF00]/5 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-xs tracking-[0.2em] text-[#CCFF00]">
                    EINLADUNGS-CODE
                  </div>
                  <button
                    data-testid="regenerate-invite-btn"
                    onClick={regenerateCode}
                    className="text-white/50 hover:text-[#CCFF00] transition-colors flex items-center gap-1 text-xs font-heading font-bold"
                    title="Neuen Code erzeugen"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Neu erzeugen
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    data-testid="invite-code-display"
                    className="flex-1 font-heading font-black text-3xl tracking-[0.4em] text-[#CCFF00]"
                  >
                    {inviteCode}
                  </div>
                  <button
                    data-testid="copy-invite-btn"
                    onClick={copyInviteCode}
                    className="p-2.5 border border-white/10 rounded-md hover:border-[#CCFF00]/40 hover:bg-[#CCFF00]/5 transition-colors"
                    title="Code kopieren"
                  >
                    {codeCopied ? (
                      <Check className="w-4 h-4 text-[#CCFF00]" />
                    ) : (
                      <Copy className="w-4 h-4 text-white/70" />
                    )}
                  </button>
                </div>
                <div className="text-xs text-white/50 mt-3">
                  Teile diesen Code mit deinen Co-Trainern. Sie können im
                  Dashboard unter &bdquo;Team beitreten&rdquo; beitreten.
                </div>
              </div>
            )}

            <div className="space-y-2 mb-4">
              {members.map((m) => (
                <div
                  key={m.id}
                  data-testid={`member-row-${m.id}`}
                  className="flex items-center gap-3 p-3 border border-white/10 rounded-md bg-[#0f1013]"
                >
                  <div
                    className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      m.role === "owner"
                        ? "bg-[#CCFF00]/10 border border-[#CCFF00]/40 text-[#CCFF00]"
                        : "bg-white/5 border border-white/15 text-white/70"
                    }`}
                  >
                    {m.role === "owner" ? (
                      <Crown className="w-4 h-4" />
                    ) : (
                      <UserCog className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm truncate">
                      {m.name}
                    </div>
                    <div className="text-xs text-white/50 truncate">
                      {m.email}
                      {m.club ? ` · ${m.club}` : ""}
                    </div>
                  </div>
                  <div
                    className={`font-display text-[10px] tracking-widest px-2 py-1 rounded-sm ${
                      m.role === "owner"
                        ? "text-[#CCFF00] border border-[#CCFF00]/40"
                        : "text-white/60 border border-white/15"
                    }`}
                  >
                    {m.role === "owner" ? "OWNER" : "CO-TRAINER"}
                  </div>
                  {team?.is_owner && m.role !== "owner" && (
                    <button
                      data-testid={`remove-member-${m.id}`}
                      onClick={() => removeMember(m.id, m.name)}
                      className="text-white/30 hover:text-red-400 transition-colors p-1"
                      title="Entfernen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {!team?.is_owner && (
              <button
                data-testid="leave-team-btn"
                onClick={leaveTeam}
                className="btn-ghost w-full justify-center border-red-500/30 hover:border-red-500/60 hover:bg-red-500/5"
              >
                <LogOut className="w-4 h-4" />
                Team verlassen
              </button>
            )}
          </div>
        </div>
      )}

      {qrPlayer && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setQrPlayer(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="qr-modal"
            className="w-full max-w-md bg-[#111214] border border-white/10 rounded-md p-6 sm:p-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
                  / QR-CODE ZUM TEST
                </div>
                <h2 className="font-heading font-black text-2xl leading-tight">
                  {qrPlayer.name}
                </h2>
                {qrPlayer.position && (
                  <div className="text-sm text-white/50 mt-1">
                    {qrPlayer.position}
                    {qrPlayer.jersey_number ? ` · #${qrPlayer.jersey_number}` : ""}
                  </div>
                )}
              </div>
              <button
                onClick={() => setQrPlayer(null)}
                className="text-white/40 hover:text-white p-1"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              data-testid="qr-code-canvas"
              className="bg-white p-4 rounded-md flex items-center justify-center"
            >
              <QRCode
                value={testLink(qrPlayer.test_token)}
                size={256}
                bgColor="#FFFFFF"
                fgColor="#050505"
                level="M"
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </div>

            <div className="mt-4 text-center">
              <div className="font-display text-[10px] tracking-widest text-white/40 mb-1">
                MIT SMARTPHONE SCANNEN
              </div>
              <p className="text-xs text-white/60 leading-relaxed">
                Der Spieler scannt den Code mit der Kamera und startet den
                Test direkt – kein Login nötig.
              </p>
            </div>

            <div className="mt-5 pt-5 border-t border-white/5 space-y-2">
              <div className="font-display text-[10px] tracking-widest text-white/40">
                TEST-LINK
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-white/70 bg-black/40 px-3 py-2 rounded truncate">
                  {testLink(qrPlayer.test_token)}
                </code>
                <button
                  data-testid="qr-copy-link-btn"
                  onClick={() => copyLink(qrPlayer.test_token)}
                  className="p-2 border border-white/10 rounded hover:border-[#CCFF00]/40 hover:bg-[#CCFF00]/5 transition-colors"
                  title="Link kopieren"
                >
                  {copied === qrPlayer.test_token ? (
                    <Check className="w-4 h-4 text-[#CCFF00]" />
                  ) : (
                    <Copy className="w-4 h-4 text-white/70" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setQrPlayer(null)}
                className="btn-ghost flex-1 justify-center"
              >
                Schließen
              </button>
              <a
                data-testid="qr-open-link-btn"
                href={testLink(qrPlayer.test_token)}
                target="_blank"
                rel="noreferrer"
                className="btn-primary flex-1 justify-center"
              >
                <ExternalLink className="w-4 h-4" />
                Test öffnen
              </a>
            </div>
          </div>
        </div>
      )}

      {showCsv && (
        <CsvImportModal
          teamId={teamId}
          onClose={() => setShowCsv(false)}
          onImported={load}
        />
      )}

      {editPlayer && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setEditPlayer(null)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveEdit}
            data-testid="edit-player-modal"
            className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#111214] border border-white/10 rounded-md p-6 sm:p-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
                  / SPIELER BEARBEITEN
                </div>
                <h2 className="font-heading font-black text-2xl">
                  {editPlayer.name}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setEditPlayer(null)}
                className="text-white/40 hover:text-white p-1"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-1 mb-4 border-b border-white/5" data-testid="edit-tabs">
              {[
                ["basic", "Grunddaten"],
                ["training", "Trainingsdaten"],
                ["notes", `Notizen (${editPlayer.notes?.length || 0})`],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`edit-tab-${k}`}
                  onClick={() => setEditTab(k)}
                  className={`px-3 py-2 text-xs font-heading font-bold uppercase tracking-widest transition-colors border-b-2 -mb-px ${
                    editTab === k
                      ? "border-[#CCFF00] text-[#CCFF00]"
                      : "border-transparent text-white/50 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {editTab === "basic" && (
              <div className="space-y-4">
                <div>
                  <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                    Name
                  </label>
                  <input
                    data-testid="edit-player-name"
                    required
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                      Position
                    </label>
                    <select
                      data-testid="edit-player-position"
                      value={editForm.position || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, position: e.target.value }))
                      }
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    >
                      {POSITIONS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                      Rückennummer
                    </label>
                    <input
                      data-testid="edit-player-number"
                      type="number"
                      min="1"
                      max="99"
                      value={editForm.jersey_number}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, jersey_number: e.target.value }))
                      }
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {editTab === "training" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Geburtsdatum</label>
                    <input
                      data-testid="edit-birthdate"
                      type="date"
                      value={editForm.birthdate || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, birthdate: e.target.value }))}
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Starker Fuß</label>
                    <select
                      data-testid="edit-strong-foot"
                      value={editForm.strong_foot || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, strong_foot: e.target.value }))}
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    >
                      <option value="">–</option>
                      <option value="rechts">rechts</option>
                      <option value="links">links</option>
                      <option value="beidfüßig">beidfüßig</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Größe (cm)</label>
                    <input
                      data-testid="edit-height"
                      type="number" min="100" max="220"
                      value={editForm.height_cm}
                      onChange={(e) => setEditForm((f) => ({ ...f, height_cm: e.target.value }))}
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Gewicht (kg)</label>
                    <input
                      data-testid="edit-weight"
                      type="number" min="20" max="150"
                      value={editForm.weight_kg}
                      onChange={(e) => setEditForm((f) => ({ ...f, weight_kg: e.target.value }))}
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Trainings anwesend</label>
                    <input
                      data-testid="edit-attendance-present"
                      type="number" min="0"
                      value={editForm.attendance_present}
                      onChange={(e) => setEditForm((f) => ({ ...f, attendance_present: e.target.value }))}
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Trainings gefehlt</label>
                    <input
                      data-testid="edit-attendance-absent"
                      type="number" min="0"
                      value={editForm.attendance_absent}
                      onChange={(e) => setEditForm((f) => ({ ...f, attendance_absent: e.target.value }))}
                      className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-display text-xs tracking-widest text-white/60 block mb-2">Verletzungs-/Belastungs-Hinweise</label>
                  <textarea
                    data-testid="edit-injury"
                    rows={2}
                    value={editForm.injury_note}
                    onChange={(e) => setEditForm((f) => ({ ...f, injury_note: e.target.value }))}
                    className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none resize-none"
                  />
                </div>
              </div>
            )}

            {editTab === "notes" && (
              <div className="space-y-3" data-testid="notes-tab">
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {(editPlayer.notes || []).length === 0 ? (
                    <div className="text-sm text-white/50 italic p-4 text-center border border-dashed border-white/10 rounded-md">
                      Noch keine Notizen.
                    </div>
                  ) : (
                    [...(editPlayer.notes || [])]
                      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                      .map((n) => (
                        <div
                          key={n.id}
                          data-testid={`note-${n.id}`}
                          className="p-3 border border-white/10 rounded-md bg-[#0a0a0b]"
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="font-display text-[10px] tracking-widest text-white/40">
                              {n.author_name} · {new Date(n.created_at).toLocaleDateString("de-DE")}
                            </div>
                            <button
                              type="button"
                              data-testid={`note-delete-${n.id}`}
                              onClick={() => removeNote(n.id)}
                              className="text-white/30 hover:text-red-400 p-0.5"
                              title="Notiz löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-sm text-white/80 whitespace-pre-wrap">{n.text}</div>
                        </div>
                      ))
                  )}
                </div>
                <div className="pt-3 border-t border-white/5 space-y-2">
                  <textarea
                    data-testid="note-input"
                    rows={2}
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Neue Notiz (z.B. Beobachtung, Verletzung, Ziel)…"
                    className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-[#CCFF00] outline-none resize-none"
                  />
                  <button
                    type="button"
                    data-testid="note-add-btn"
                    onClick={addNote}
                    disabled={!newNote.trim()}
                    className="btn-ghost !py-2 !px-4 text-xs disabled:opacity-40 w-full justify-center"
                  >
                    <Plus className="w-4 h-4" />
                    Notiz hinzufügen
                  </button>
                </div>
              </div>
            )}

            {editPlayer.test_status === "completed" && editTab === "basic" && (
              <div className="mt-4 text-xs text-white/50">
                Hinweis: Änderungen am Namen wirken auch auf bereits generierte PDFs
                (bei Neuerstellung).
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setEditPlayer(null)}
                className="btn-ghost flex-1 justify-center"
              >
                Schließen
              </button>
              {editTab !== "notes" && (
                <button
                  type="submit"
                  data-testid="edit-player-submit"
                  className="btn-primary flex-1 justify-center"
                >
                  Speichern
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
